import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtEur, fmtEur2 } from "@/lib/format";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { statusLabel, statusColor } from "@/lib/format";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { TrendingUp, Clock, AlertTriangle, Users, ArrowUpRight, Receipt, Sparkles, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const KpiCard = ({ tid, label, value, delta, icon: Icon, tone = "primary" }) => {
  const toneMap = {
    primary: "text-zen-primary bg-zen-primary/10",
    alert: "text-zen-alert bg-zen-alert/10",
    info: "text-zen-info bg-zen-info/15",
    ink: "text-zen-ink bg-zen-subtle/25",
  };
  return (
    <div data-testid={tid} className="zen-card p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="zen-label">{label}</div>
          <div className="mt-3 font-heading text-3xl text-zen-ink">{value}</div>
          {delta && <div className="mt-1 text-sm text-zen-ink2">{delta}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${toneMap[tone]}`}>
          <Icon size={18} strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    const [rep, tks, cls] = await Promise.all([
      api.get("/reports/dashboard"),
      api.get("/tasks"),
      api.get("/clients"),
    ]);
    setData(rep.data);
    setTasks(tks.data);
    const cm = {}; cls.data.forEach((c) => cm[c.client_id] = c);
    setClientsMap(cm);
  };
  useEffect(() => { load(); }, []);

  const genInsights = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/reports/ai-insights");
      setAiInsights(data.insights);
    } catch { toast.error("Errore AI, riprova"); }
    finally { setAiLoading(false); }
  };

  if (!data) return <div className="text-zen-ink2">Caricamento…</div>;

  const upcoming = tasks
    .filter(t => !t.completed && t.due_date)
    .sort((a,b) => a.due_date.localeCompare(b.due_date))
    .slice(0, 5);

  const topProjects = [...data.per_project]
    .sort((a,b) => b.revenue - a.revenue)
    .slice(0, 4);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <span className="zen-label">Panoramica</span>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl">Buongiorno, ecco il tuo studio oggi.</h1>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard tid="kpi-ricavi" label="Ricavi incassati" value={fmtEur(data.kpi.total_revenue)}
          delta={`In attesa: ${fmtEur(data.kpi.pending_revenue)}`} icon={TrendingUp} tone="primary"/>
        <KpiCard tid="kpi-ore" label="Ore fatturabili" value={data.kpi.billable_hours.toFixed(1)}
          delta={`${data.kpi.active_projects} progetti attivi`} icon={Clock} tone="info"/>
        <KpiCard tid="kpi-margine" label="Marginalità media" value={`${data.kpi.avg_margin_pct}%`}
          delta="Ricavi netto costi" icon={ArrowUpRight} tone="ink"/>
        <KpiCard tid="kpi-scadute" label="Fatture scadute" value={fmtEur(data.kpi.overdue_amount)}
          delta={`${data.kpi.overdue_count} fatture da recuperare`} icon={AlertTriangle} tone="alert"/>
      </div>

      {/* Chart + tasks */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 zen-card p-6" data-testid="chart-revenue">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="zen-label">Andamento</span>
              <h2 className="font-heading text-xl mt-1">Ricavi ultimi 6 mesi</h2>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly_revenue}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4A947D" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#4A947D" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E3D8"/>
                <XAxis dataKey="month" stroke="#78848D" fontSize={12}/>
                <YAxis stroke="#78848D" fontSize={12} tickFormatter={(v) => `€${v}`}/>
                <Tooltip formatter={(v) => fmtEur2(v)} contentStyle={{borderRadius: 12, border: "1px solid #E5E3D8"}}/>
                <Area type="monotone" dataKey="revenue" stroke="#4A947D" strokeWidth={2} fill="url(#rev)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="zen-card p-6" data-testid="upcoming-tasks">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="zen-label">Prossime scadenze</span>
              <h2 className="font-heading text-xl mt-1">Attività da fare</h2>
            </div>
            <Link to="/attivita" className="text-sm text-zen-primary hover:underline">Tutte</Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-zen-ink2 text-sm py-6 text-center">Nessuna attività in scadenza. Respira.</p>
          ) : (
            <ul className="space-y-3">
              {upcoming.map((t) => (
                <li key={t.task_id} className="flex items-start gap-3 p-3 rounded-xl bg-zen-bg">
                  <div className={`w-2 h-2 rounded-full mt-2 ${t.priority === "alta" ? "bg-zen-alert" : t.priority === "media" ? "bg-zen-info" : "bg-zen-subtle"}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zen-ink truncate">{t.title}</div>
                    <div className="text-xs text-zen-ink2 mt-0.5">
                      {t.due_date && new Date(t.due_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                      {t.client_id && clientsMap[t.client_id] && ` · ${clientsMap[t.client_id].name}`}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Projects margin */}
      <div className="zen-card p-6" data-testid="top-projects">
        <div className="flex items-center justify-between mb-5">
          <div>
            <span className="zen-label">Progetti</span>
            <h2 className="font-heading text-xl mt-1">Top per ricavi</h2>
          </div>
          <Link to="/progetti" className="text-sm text-zen-primary hover:underline flex items-center gap-1">
            Vedi tutti <ArrowUpRight size={14}/>
          </Link>
        </div>
        {topProjects.length === 0 ? (
          <p className="text-zen-ink2 text-sm py-6 text-center">Nessun progetto ancora.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {topProjects.map((p) => (
              <div key={p.project_id} className="p-4 rounded-xl border border-zen-border">
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <div className="font-medium text-zen-ink truncate">{p.name}</div>
                    <div className="text-xs text-zen-ink2 mt-0.5">{p.hours_logged.toFixed(1)}h · margine {p.margin_pct}%</div>
                  </div>
                  <Badge className={`${statusColor[p.status]} rounded-full font-normal`}>{statusLabel[p.status]}</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <Progress value={p.progress} className="h-1.5 flex-1"/>
                  <span className="text-xs text-zen-ink2 w-14 text-right">{fmtEur(p.revenue)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Insights (Claude live) */}
      <div className="zen-card p-6" data-testid="dashboard-ai-insights">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-zen-primary/10 text-zen-primary flex items-center justify-center">
              <Sparkles size={18} strokeWidth={1.5}/>
            </div>
            <div>
              <span className="zen-label">Powered by Claude Sonnet 4.5</span>
              <h2 className="font-heading text-xl mt-0.5">Insight AI del tuo studio</h2>
            </div>
          </div>
          <Button
            data-testid="dashboard-gen-insights"
            size="sm"
            onClick={genInsights}
            disabled={aiLoading}
            className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
          >
            <Sparkles size={13} className="mr-1.5"/>
            {aiLoading ? "Analizzo…" : (aiInsights ? "Rigenera" : "Genera insight")}
          </Button>
        </div>
        {!aiInsights ? (
          <p className="text-zen-ink2 text-sm">
            Ottieni un&apos;analisi personalizzata basata sui tuoi ricavi, marginalità, clienti e fatture scadute.
          </p>
        ) : (
          <div
            className="whitespace-pre-wrap text-zen-ink leading-relaxed text-sm"
            data-testid="dashboard-ai-insights-body"
          >{aiInsights}</div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { to: "/inbox", icon: Receipt, label: "Apri inbox", desc: "Email + WhatsApp" },
          { to: "/preventivi", icon: FileText, label: "Nuovo preventivo", desc: "PDF scaricabile" },
          { to: "/fatture", icon: AlertTriangle, label: "Recupera crediti", desc: "Solleciti AI" },
        ].map((a) => (
          <Link key={a.to} to={a.to} data-testid={`quick-${a.to.slice(1)}`} className="zen-card p-5 hover:-translate-y-0.5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-zen-subtle/30 text-zen-ink flex items-center justify-center"><a.icon size={18} strokeWidth={1.5}/></div>
              <div>
                <div className="font-medium">{a.label}</div>
                <div className="text-xs text-zen-ink2">{a.desc}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
